import React from 'react';
import { Link } from 'react-router-dom';
import {
  TERMS_CONTACT_EMAIL,
  TERMS_LAST_UPDATED,
} from '../../legal/termsConstants';
import { LEGAL_ENTITY_NAME, BUSINESS_ADDRESS } from '../../legal/privacyConstants';

const tocItems = [
  { id: 'acceptance', label: 'Agreement to terms' },
  { id: 'eligibility', label: 'Eligibility and accounts' },
  { id: 'responsibilities', label: 'Your responsibilities' },
  { id: 'prohibited', label: 'Prohibited conduct' },
  { id: 'fees', label: 'Fees and subscriptions' },
  { id: 'content-ip', label: 'Your content and our IP' },
  { id: 'third-party', label: 'Third-party services' },
  { id: 'disclaimers', label: 'Disclaimers' },
  { id: 'liability', label: 'Limitation of liability' },
  { id: 'indemnity', label: 'Indemnity' },
  { id: 'suspension', label: 'Suspension and termination' },
  { id: 'changes', label: 'Changes to these terms' },
  { id: 'governing-law', label: 'Governing law and disputes' },
  { id: 'contact', label: 'Contact' },
];

const sectionClass =
  'scroll-mt-24 mb-14 last:mb-0 border-b border-gray-200 dark:border-gray-700 pb-14 last:border-0 last:pb-0';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <header className="border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <span className="text-lg font-semibold text-primary-600 dark:text-primary-400">
            WorkLoop
          </span>
          <nav className="flex flex-wrap items-center gap-4 text-sm">
            <Link
              to="/privacy"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              Privacy Policy
            </Link>
            <Link
              to="/support"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              Support
            </Link>
            <Link
              to="/app"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              About
            </Link>
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              Create account
            </Link>
          </nav>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 lg:py-14">
        <div className="lg:grid lg:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] lg:gap-12 xl:gap-16">
          <aside className="hidden lg:block">
            <nav
              className="sticky top-28 space-y-1 text-sm border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-white dark:bg-gray-800 shadow-sm"
              aria-label="On this page"
            >
              <p className="font-semibold text-gray-900 dark:text-white mb-3 px-2">
                On this page
              </p>
              <ul className="space-y-1">
                {tocItems.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="block px-2 py-1.5 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          <main className="min-w-0 max-w-3xl">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">
              Terms and Conditions
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Last updated: {TERMS_LAST_UPDATED}
            </p>

            <div className="mt-8 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 sm:p-8 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Summary
              </h2>
              <ul className="list-disc list-outside pl-5 space-y-2 text-gray-700 dark:text-gray-300 text-sm sm:text-base">
                <li>
                  By using WorkLoop you agree to these terms and our{' '}
                  <Link to="/privacy" className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
                    Privacy Policy
                  </Link>
                  .
                </li>
                <li>
                  WorkLoop is for organizational and professional use. Your employer or workspace
                  administrator may manage access and enforce additional rules.
                </li>
                <li>
                  You must use the service lawfully, protect your account, and not misuse messaging,
                  files, integrations, or the platform.
                </li>
                <li>
                  The service is provided as described in these terms and applicable agreements with
                  your organization; liability limits and disclaimers apply where the law allows.
                </li>
                <li>
                  Questions? See the{' '}
                  <a href="#contact" className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
                    Contact
                  </a>{' '}
                  section.
                </li>
              </ul>
            </div>

            <div className="mt-12 space-y-0 text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
              <section id="acceptance" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  1. Agreement to terms
                </h2>
                <p className="mb-4">
                  These Terms and Conditions (“Terms”) govern your access to and use of WorkLoop, a
                  corporate communication and collaboration platform (the “Service”), whether you
                  access it through our website, desktop clients, or mobile applications.
                </p>
                <p className="mb-4">
                  By creating an account, clicking to accept, or otherwise using the Service, you
                  agree to these Terms and our{' '}
                  <Link to="/privacy" className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
                    Privacy Policy
                  </Link>
                  . If you do not agree, do not use the Service.
                </p>
                <p>
                  If you use WorkLoop on behalf of an organization, you represent that you have
                  authority to bind that organization, and “you” includes both you and the
                  organization. Your organization may also have a separate written agreement with
                  us; if there is a conflict between these Terms and that agreement, the separate
                  agreement controls for that customer relationship to the extent of the conflict.
                </p>
              </section>

              <section id="eligibility" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  2. Eligibility and accounts
                </h2>
                <p className="mb-4">
                  The Service is intended for business and professional users. You must provide
                  accurate registration information and keep it up to date. You are responsible for
                  maintaining the confidentiality of your credentials and for all activity under
                  your account, except where caused by our gross negligence or willful misconduct.
                </p>
                <p className="mb-4">
                  Organizations may register for WorkLoop and provision users. Workspace or
                  organization administrators may create, suspend, or remove accounts, configure
                  security settings, and access certain data in accordance with product capabilities
                  and the organization’s policies.
                </p>
                <p>
                  Password reset and similar requests may require administrator approval where that
                  workflow is enabled for your workspace.
                </p>
              </section>

              <section id="responsibilities" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  3. Your responsibilities
                </h2>
                <p className="mb-4">You agree to:</p>
                <ul className="list-disc list-outside pl-5 space-y-2">
                  <li>Use the Service only in compliance with applicable laws and regulations;</li>
                  <li>
                    Comply with your employer’s or organization’s acceptable-use and security
                    policies when using a workspace they control;
                  </li>
                  <li>
                    Not share your login credentials or allow others to access your account except
                    as permitted by your organization’s policies;
                  </li>
                  <li>
                    Use channels, messages, files, tasks, announcements, reminders, and integrations
                    in a professional manner appropriate to a workplace tool.
                  </li>
                </ul>
              </section>

              <section id="prohibited" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  4. Prohibited conduct
                </h2>
                <p className="mb-4">You must not:</p>
                <ul className="list-disc list-outside pl-5 space-y-2 mb-4">
                  <li>
                    Use the Service for unlawful purposes or to transmit illegal, infringing, or
                    harmful content;
                  </li>
                  <li>
                    Harass, threaten, or abuse others, or distribute malware, spam, or deceptive
                    content;
                  </li>
                  <li>
                    Attempt to gain unauthorized access to the Service, other users’ accounts, or
                    our systems, or probe, scan, or test vulnerabilities without authorization;
                  </li>
                  <li>
                    Reverse engineer, decompile, or disassemble the Service except where
                    prohibited by applicable law;
                  </li>
                  <li>
                    Interfere with or disrupt the Service, including by overloading infrastructure
                    or circumventing rate limits or security measures;
                  </li>
                  <li>
                    Use automated means (such as bots or scrapers) to access the Service in a way
                    that violates these Terms or imposes an unreasonable load, without our prior
                    written consent;
                  </li>
                  <li>
                    Misrepresent your identity or affiliation, or use another user’s account without
                    permission.
                  </li>
                </ul>
                <p>
                  We may investigate violations and cooperate with law enforcement or your
                  organization where appropriate.
                </p>
              </section>

              <section id="fees" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  5. Fees and subscriptions
                </h2>
                <p className="mb-4">
                  Access to WorkLoop for end users within an organization is typically provided under
                  arrangements between us and your organization (or your employer). The consumer
                  web and mobile applications may not process direct payments from individual users.
                </p>
                <p>
                  If your organization purchases paid features or enterprise services, fees,
                  invoicing, taxes, and renewal terms are set out in the applicable order form,
                  statement of work, or other contract between us and that organization.
                </p>
              </section>

              <section id="content-ip" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  6. Your content and our intellectual property
                </h2>
                <p className="mb-4">
                  You retain ownership of content you submit to the Service (such as messages, files,
                  task descriptions, and profile information), subject to the rights you grant
                  below. You are responsible for ensuring you have the rights to submit that content.
                </p>
                <p className="mb-4">
                  You grant us a worldwide, non-exclusive license to host, store, reproduce, display,
                  and process your content solely to provide, secure, and improve the Service and to
                  comply with law. You also grant us a license to use aggregated or de-identified
                  data derived from use of the Service for analytics and improvement, where permitted
                  by law and our Privacy Policy.
                </p>
                <p className="mb-4">
                  The Service, including its software, branding, documentation, and design, is owned
                  by us or our licensors and is protected by intellectual property laws. Except for
                  the limited right to use the Service under these Terms, we grant you no rights to
                  our intellectual property.
                </p>
                <p>
                  Feedback you provide may be used by us without obligation or compensation to you.
                </p>
              </section>

              <section id="third-party" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  7. Third-party services
                </h2>
                <p className="mb-4">
                  The Service may include links to third-party websites or allow integrations with
                  third-party systems (for example, connectors your administrator enables). Those
                  third parties are not under our control; their terms and privacy practices apply
                  to your use of their services.
                </p>
                <p>
                  We are not responsible for third-party content, availability, or actions. Your
                  use of integrations is at your and your organization’s discretion and risk.
                </p>
              </section>

              <section id="disclaimers" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  8. Disclaimers
                </h2>
                <p className="mb-4">
                  To the fullest extent permitted by applicable law, the Service is provided on an
                  “as is” and “as available” basis. We disclaim all warranties, whether express,
                  implied, or statutory, including implied warranties of merchantability, fitness for
                  a particular purpose, title, and non-infringement.
                </p>
                <p>
                  We do not warrant that the Service will be uninterrupted, error-free, or free of
                  harmful components. We are not responsible for user-generated content, actions of
                  other users, or third-party services.
                </p>
              </section>

              <section id="liability" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  9. Limitation of liability
                </h2>
                <p className="mb-4">
                  To the fullest extent permitted by applicable law, neither we nor our affiliates,
                  directors, employees, or suppliers will be liable for any indirect, incidental,
                  special, consequential, or punitive damages, or for loss of profits, data,
                  goodwill, or business opportunities, arising out of or related to your use of the
                  Service, even if we have been advised of the possibility of such damages.
                </p>
                <p className="mb-4">
                  To the fullest extent permitted by applicable law, our aggregate liability for any
                  claim arising out of or relating to the Service or these Terms will not exceed the
                  greater of (a) the amounts you paid us for the Service in the twelve months before
                  the claim (if any) or (b) one hundred (100) currency units in the jurisdiction of
                  your organization’s principal place of business, unless a higher minimum applies
                  under mandatory law.
                </p>
                <p>
                  Some jurisdictions do not allow certain limitations; in those jurisdictions, our
                  liability is limited to the maximum extent permitted by law.
                </p>
              </section>

              <section id="indemnity" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  10. Indemnity
                </h2>
                <p>
                  To the extent permitted by law, you will defend, indemnify, and hold harmless us
                  and our affiliates, officers, directors, employees, and agents from and against any
                  claims, damages, losses, liabilities, costs, and expenses (including reasonable
                  attorneys’ fees) arising out of or related to your content, your use of the
                  Service in violation of these Terms, or your violation of law or third-party
                  rights. We may assume exclusive defense and control of any matter subject to
                  indemnification, at your expense, and you agree to cooperate with our defense.
                </p>
              </section>

              <section id="suspension" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  11. Suspension and termination
                </h2>
                <p className="mb-4">
                  We may suspend or terminate your access to the Service, with or without notice, if
                  we reasonably believe you have violated these Terms, pose a security risk, or if
                  required by law. Your organization may also suspend or remove your access.
                </p>
                <p className="mb-4">
                  You may stop using the Service at any time. Upon termination, your right to use the
                  Service ceases. Provisions that by their nature should survive (including
                  intellectual property, disclaimers, limitation of liability, indemnity, and
                  governing law) will survive.
                </p>
                <p>
                  Data handling after termination may be subject to our Privacy Policy, your
                  organization’s retention settings, and applicable law.
                </p>
              </section>

              <section id="changes" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  12. Changes to these terms
                </h2>
                <p className="mb-4">
                  We may modify these Terms from time to time. We will update the “Last updated” date
                  above and, where appropriate, provide additional notice (such as an in-app
                  message or email).
                </p>
                <p>
                  If you continue to use the Service after changes become effective, you accept the
                  revised Terms, to the extent permitted by law. If you do not agree, you must stop
                  using the Service.
                </p>
              </section>

              <section id="governing-law" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  13. Governing law and disputes
                </h2>
                <p className="mb-4">
                  These Terms are governed by the laws of{' '}
                  <span className="text-gray-900 dark:text-white font-medium">Wagholi</span>
                  , without regard to conflict-of-law principles, except where mandatory rules of your
                  consumer or employment jurisdiction apply and cannot be waived.
                </p>
                <p>
                  Courts or tribunals located in{' '}
                  <span className="text-gray-900 dark:text-white font-medium">Wagholi</span>{' '}
                  will have exclusive jurisdiction over disputes arising from these Terms or the
                  Service, subject to mandatory provisions of law. You and we consent to that
                  jurisdiction and venue.
                </p>
              </section>

              <section id="contact" className={sectionClass}>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  14. Contact
                </h2>
                <p className="mb-4">
                  For questions about these Terms, contact us at:
                </p>
                <ul className="space-y-2 not-prose">
                  <li>
                    <span className="text-gray-500 dark:text-gray-400">Email: </span>
                    <a
                      href={`mailto:${TERMS_CONTACT_EMAIL}`}
                      className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                    >
                      {TERMS_CONTACT_EMAIL}
                    </a>
                  </li>
                  <li>
                    <span className="text-gray-500 dark:text-gray-400">Company: </span>
                    <span className="text-gray-900 dark:text-white">{LEGAL_ENTITY_NAME}</span>
                  </li>
                  <li>
                    <span className="text-gray-500 dark:text-gray-400">Address: </span>
                    <span className="text-gray-900 dark:text-white">{BUSINESS_ADDRESS}</span>
                  </li>
                </ul>
                <p className="mt-6 text-xs text-gray-500 dark:text-gray-400">
                  These Terms are provided for transparency and do not constitute legal advice.
                  Please have qualified counsel review them for your jurisdiction and deployment.
                </p>
              </section>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
